import { MonetaryValueResponse } from '../../api/core/models/api/monetary-value-response';
import { ValueResponse } from '../../api/core/models/api/value-response';
import { ClientBoard } from '../../model/client-model/client-board';
import { ClientVariable } from '../../model/client-model/client-variable';
import { MeasurementSystem } from '../../model/enums/measurement-system';
import { IVariable } from '../../services/climate-engine/model/variable';
import { UnitConversionService } from '../../services/unit-conversion/unit-conversion.service';
import { convertCase, TargetCase } from '../utilities/convert-case';
import { ApiEstimateResponse } from '../../api/data-definitions/models';

export function toClientVariables({
  variableDefinitions,
  variableValues,
  variableEstimates,
  measurementSystem,
  unitConversionService,
}: {
  variableDefinitions: Record<IVariable['variable'], IVariable>;
  variableValues: Map<string, ValueResponse | MonetaryValueResponse>;
  variableEstimates: ApiEstimateResponse[];
  measurementSystem: NonNullable<ClientBoard['boardSettings']['measurementSystem']>;
  unitConversionService: UnitConversionService;
}): ClientVariable[] {
  const isVariableNew = (variableName: IVariable['variable']): boolean =>
    !variableValues || !variableValues.has(variableName);
  const transformValue = (value: number, unitOfMeasure: IVariable['unitOfMeasure']): number =>
    unitConversionService.transformValue(MeasurementSystem.Metric, measurementSystem, unitOfMeasure, value);

  return Object.keys(variableDefinitions)
    .map((variableDefinitionId) => {
      const variableValue = variableValues.get(variableDefinitionId);
      const variableDefinitionData = variableDefinitions[variableDefinitionId];
      if (!variableValue) {
        return null;
      }
      if (!variableDefinitionData) {
        throw new Error(`Variable definition not found for variable ${variableDefinitionId}`);
      }

      // General:
      const valueId = variableValue.id;
      const name = variableDefinitionId;
      const isNew = isVariableNew(variableDefinitionId);

      // Units and values:
      const metricUnit = variableDefinitionData.unitOfMeasure;
      const unit = unitConversionService.transformName(
        MeasurementSystem.Metric,
        measurementSystem,
        variableDefinitionData.unitOfMeasure
      );
      const metricValue = variableValue.value;
      const value = transformValue(<number>metricValue, variableDefinitionData.unitOfMeasure);
      const min = transformValue(<number>variableDefinitionData.min, variableDefinitionData.unitOfMeasure);
      const max = transformValue(<number>variableDefinitionData.max, variableDefinitionData.unitOfMeasure);
      const currency = !!variableValue && 'currency' in variableValue ? variableValue.currency : undefined;
      const inventoryId = variableValue.inventoryId;

      // Scaling:
      const scaling = variableEstimates.find((e) => e.variable === variableDefinitionId);
      const hasScaling = !!scaling;
      const approximationType = scaling?.scalingType
        ? <ClientVariable['approximationType']>(
            convertCase(<'CONSTANT' | 'PER_CAPITA'>scaling.scalingType, TargetCase.CAMEL)
          )
        : undefined;
      const approximation = scaling?.value;
      const sourceDescription = scaling?.referenceDescription;
      const sourceUrl = scaling?.reference;

      return {
        valueId,
        name,
        isNew,
        metricUnit,
        unit,
        metricValue,
        value,
        min,
        max,
        hasScaling,
        approximationType,
        approximation,
        sourceDescription,
        sourceUrl,
        currency,
        inventoryId,
      };
    })
    .filter((v) => v !== null) as ClientVariable[];
}
